import React, { useEffect, useState } from 'react';
import { useTheme } from '@components/theme';
import { IStyleQuizAnswerOption } from '@graphql/StyleQuiz/queries/get-style-quiz-question.graphql';
import classNames from 'classnames';
import { useMinBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import styles from './RoundedOptions.module.scss';
import Check from '../../Check/Check';

export interface ISelectableOption extends IStyleQuizAnswerOption {
  selected: boolean;
}

type IDirection = 'row' | 'column';
type IAlign = 'start' | 'flex-start' | 'center' | 'end' | 'flex-end';
type IJustify = 'left' | 'center' | 'right';

interface IResponsiveParam {
  radius?: number;
  direction?: IDirection;
  align?: IAlign;
  justify?: IJustify;
  gap?: number;
  iconHeight?: number;
}

interface IAnswer {
  optionId: GraphQLID;
  questionId: GraphQLID;
  text?: string;
  inspirationImageUrls?: string[];
  counter?: number;
}

export interface IRoundedOptionsProps {
  className?: string;
  dataTestPrefix?: string;
  initialAnswers: IAnswer[] | IAnswer;
  answerOptions: IStyleQuizAnswerOption[];
  onOptionSelect: (option: ISelectableOption) => void;
  onOptionUnselect?: (option: ISelectableOption, counter?: number) => void;
  multiSelect?: boolean;
  multiSelectCounters?: boolean;
  radius?: number;
  direction?: IDirection;
  align?: IAlign;
  justify?: IJustify;
  gap?: number;
  iconHeight?: number;
  breakpoints?: { [key: number]: IResponsiveParam };
}
export default function RoundedOptions({
  className = '',
  dataTestPrefix = '',
  initialAnswers,
  answerOptions,
  onOptionSelect,
  onOptionUnselect = () => {},
  multiSelect = false,
  multiSelectCounters = false,
  radius = 138,
  direction = 'row',
  align = 'center',
  justify = 'center',
  gap = 40,
  iconHeight = 30,
  breakpoints = {}
}: IRoundedOptionsProps) {
  const theme = useTheme();

  const [options, setOptions] = useState<ISelectableOption[]>([]);

  useEffect(() => {
    const answers = Array.isArray(initialAnswers) ? initialAnswers : [initialAnswers];

    setOptions(answerOptions.map((option) => {
      const answer = answers.find((_answer: IAnswer) => option.id === _answer.optionId);

      return {
        ...option,
        selected: !!answer,
        counter: answer?.counter || 0,
      };
    }));
  }, [initialAnswers, answerOptions]);

  const defaultValues = {
    radius, direction, align, justify, gap
  };
  const responsiveSize: number = Number(Object.keys(breakpoints).find(
    (key: string) => {
      return useMinBreakpoint(Number(key));
    }
  )) ?? 0;

  const responsive = {
    direction: breakpoints[responsiveSize]?.direction ?? defaultValues.direction,
    align: breakpoints[responsiveSize]?.align ?? defaultValues.align,
    justify: breakpoints[responsiveSize]?.justify ?? defaultValues.justify,
    radius: breakpoints[responsiveSize]?.radius ?? defaultValues.radius,
    gap: breakpoints[responsiveSize]?.gap ?? defaultValues.gap,
  };

  const handleOptionClick = (clickedOption: ISelectableOption) => {
    const newOptions = options.map((option) => ({
      ...option,
      // eslint-disable-next-line no-nested-ternary
      selected: option.id === clickedOption.id
        ? multiSelect ? !option.selected : true
        : multiSelect ? option.selected : false,
    }));
    setOptions(newOptions);

    const newClickedOption = newOptions.find((option) => option.id === clickedOption.id);
    if (newClickedOption?.selected) {
      onOptionSelect(newClickedOption);
    } else if (onOptionUnselect !== undefined) {
      onOptionUnselect(newClickedOption!, 0);
    }
  };

  const handleOptionIncrementClick =
    (clickedOption: ISelectableOption) => onOptionSelect(clickedOption);
  const handleOptionDecrementClick =
    (clickedOption: ISelectableOption) => onOptionUnselect(clickedOption);

  return (
    <ul
      className={classNames(styles.Options, className)}
      style={{
        flexDirection: responsive.direction,
        alignItems: responsive.align,
        justifyContent: responsive.justify,
        gap: responsive.gap
      }}
    >
      {options.map((option, idx) => (
        <li className={styles.OptionWrapper} key={option.title}>
          <button
            data-test={`${dataTestPrefix ? `${dataTestPrefix}-` : ''}style-quiz-option-${idx}`}
            className={
              classNames(
                styles.Option,
                theme.styles.Caption,
                { [styles.selected]: option.selected }
              )
            }
            style={{
              width: responsive.radius,
              height: responsive.radius,
            }}
            type="button"
            onClick={() => handleOptionClick(option)}
          >
            {option.selected && (
              <div data-test="selected" className={styles.Checkmark}>
                <span><Check /></span>
              </div>
            )}
            <span className={styles.Icon}>
              {option.iconPath && <img height={iconHeight} aria-hidden alt="icon" src={option.iconPath} />}
            </span>
            <span className={classNames(theme.styles.Body, styles.Title)}>{option.title}</span>
          </button>

          { multiSelectCounters && option.selected && (
            <div className={styles.CounterContainer}>
              <span
                onClick={() => handleOptionDecrementClick(option)}
                onKeyDown={() => {}}
                className={styles.Ellipsis}
                role={'button'}
                tabIndex={0}
              > -
              </span>
              <span className={styles.Counter}>{ option.counter }</span>
              <span
                onClick={() => handleOptionIncrementClick(option)}
                onKeyDown={() => {}}
                className={styles.Ellipsis}
                role={'button'}
                tabIndex={0}
              >+
              </span>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
